import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Unstable_Grid2 as Grid,
  Button,
  IconButton,
} from '@mui/material'
import { AddCircle, Close } from '@mui/icons-material'
import { makeStyles } from 'tss-react/mui'
import messages from '@utils/messages'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import LiquidMaterialAutoSuggestAdapter from '@src/components/LiquidMaterialAutoSuggestAdapter'
import LiquidQuantityInputAdapter from '@src/components/LiquidQuantityInputAdapter'
import AlertDialog from '@src/components/AlertDialog'
import {
  useGlobalSnackbar,
  SnackbarType,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { useCreateCustomPromotionalBundleMutation } from '@src/mutations/CreateCustomBundleMutation.generated'
import { PromotionalBundle } from '@src/types/graphql-types'

const useStyles = makeStyles()((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.background.grey,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.secondary.light,
  },
  dialogActionsRoot: {
    padding: '8px 24px',
  },
  submitButton: {
    minWidth: '182px',
    marginLeft: theme.spacing(4),
  },
  cancelButton: {
    color: theme.palette.grey[600],
    borderWidth: '2px',
    minWidth: '120px',
    borderColor: theme.palette.grey[50],
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(12),
    padding: theme.spacing(0, 3),
  },
  dialogWidth: {
    maxWidth: theme.typography.pxToRem(660),
  },
  overrideContent: {
    paddingTop: `${theme.spacing(2)} !important`,
  },
}))

const defaultBundleItem = {
  materialNumber: '',
  quantity: 1,
}
const initialValues = {
  bundleDescription: '',
  bundleItems: [
    defaultBundleItem,
    defaultBundleItem,
    defaultBundleItem,
    defaultBundleItem,
    defaultBundleItem,
  ],
}

const filterBundleItems = (bundleItems) =>
  bundleItems.filter(({ materialNumber }) => materialNumber)

interface CreateCustomBundleProps {
  setCreateCustomBundleModal: React.Dispatch<React.SetStateAction<boolean>>
  materialNumber: string
  bundleData: PromotionalBundle[]
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}

const CreateCustomBundleModal: React.FC<CreateCustomBundleProps> = ({
  setCreateCustomBundleModal,
  setShowModal,
}) => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [createCustomBundle, { loading }] =
    useCreateCustomPromotionalBundleMutation()

  const validateForm = (values) => {
    const filteredBundleItems = filterBundleItems(values.bundleItems)

    if (filteredBundleItems.length < 2) {
      return { bundleItems: formatMessage(messages.CUSTOM_BUNDLE_ITEMS_ERROR) }
    }
    return {}
  }

  const handleSubmit = async ({ bundleDescription, bundleItems }) => {
    const bundleRequest = {
      bundleDescription,
      bundleItems: filterBundleItems(bundleItems),
    }
    await createCustomBundle({
      variables: { input: bundleRequest },
    })

    setGlobalSnackbarState({
      open: true,
      message: formatMessage(messages.CUSTOM_BUNDLE_REQUEST_SUCCESS),
      variant: SnackbarType.Success,
    })
    setCreateCustomBundleModal(false)
    setShowModal(false)
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateForm}
    >
      {({ values, submitForm, errors }) => (
        <Dialog classes={{ paperWidthSm: classes.dialogWidth }} fullWidth open>
          <DialogTitle className={classes.dialogTitle}>
            {formatMessage(messages.CREATE_CUSTOM_BUNDLE)}
            <IconButton
              className={classes.closeButton}
              disabled={loading}
              onClick={() => setOpenCancelModal(true)}
              size="large"
            >
              <Close fontSize="large" onClick={() => setShowModal(false)} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={classes.overrideContent}>
            <Form>
              <Grid container spacing={6}>
                <Grid xs={12}>
                  <DialogContentText mb={3}>
                    {formatMessage(messages.CUSTOM_BUNDLE_EXPLANATION)}
                  </DialogContentText>
                </Grid>
                <Grid
                  xs={12}
                  style={
                    !errors.bundleItems ? { marginBottom: '17px' } : undefined
                  }
                >
                  <Field
                    label={formatMessage(messages.CUSTOM_BUNDLE_DESCRIPTION)}
                    name="bundleDescription"
                    filled
                    multiline
                    component={LiquidInputAdapter}
                    disabled={loading}
                  />
                </Grid>
                <ErrorMessage name="bundleItems">
                  {(message) => (
                    <div className={classes.errorMessage}>{message}</div>
                  )}
                </ErrorMessage>
                <FieldArray name="bundleItems">
                  {({ push }) => (
                    <>
                      {values.bundleItems.map((_item, index) => (
                        <Grid
                          container
                          spacing={2}
                          key={index}
                          p={3}
                          width={'100%'}
                        >
                          <Grid xs={10}>
                            <Field
                              name={`bundleItems[${index}].materialNumber`}
                              label={
                                index === 0 &&
                                formatMessage(messages.SKU_PLUS_PACK_SIZE)
                              }
                              component={LiquidMaterialAutoSuggestAdapter}
                              placeholder={formatMessage(
                                messages.ENTER_SKU_PLACEHOLDER
                              )}
                              disabled={loading}
                              filled
                            />
                          </Grid>
                          <Grid xs={2}>
                            <Field
                              name={`bundleItems[${index}].quantity`}
                              label={
                                index === 0 && formatMessage(messages.QUANTITY)
                              }
                              component={LiquidQuantityInputAdapter}
                              min={1}
                              max={999}
                              disabled={loading}
                              filled
                            />
                          </Grid>
                        </Grid>
                      ))}
                      <Grid xs={12}>
                        <Button
                          color="secondary"
                          startIcon={<AddCircle fontSize="large" />}
                          onClick={() => push(defaultBundleItem)}
                        >
                          {formatMessage(messages.ADD_ROW)}
                        </Button>
                      </Grid>
                    </>
                  )}
                </FieldArray>
              </Grid>
            </Form>
          </DialogContent>
          <DialogActions
            disableSpacing
            classes={{
              root: classes.dialogActionsRoot,
            }}
          >
            <Button
              className={classes.cancelButton}
              variant="outlined"
              onClick={() => setOpenCancelModal(true)}
            >
              {formatMessage(messages.CANCEL)}
            </Button>
            <AlertDialog
              open={openCancelModal}
              description={formatMessage(messages.CANCEL_CART_CONFIRM_TEXT)}
              cancelText={formatMessage(messages.CANCEL_CART_NO)}
              confirmText={formatMessage(messages.CANCEL_CART_YES)}
              onCancel={() => setOpenCancelModal(false)}
              onConfirm={() => setCreateCustomBundleModal(false)}
            />
            <Button
              className={classes.submitButton}
              color="primary"
              variant="contained"
              onClick={() => submitForm()}
              disabled={loading}
            >
              {formatMessage(messages.SUBMIT)}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  )
}

export default CreateCustomBundleModal
